<template>
  <div class="apply-page">
    <form class="apply-form" @submit.prevent="submitApplication">
      <h2>{{ $t('apply.title') }}</h2>

      <div class="form-group">
        <input v-model="form.name" type="text" :placeholder="$t('apply.name')" required />
        <input v-model="form.surname" type="text" :placeholder="$t('apply.surname')" required />
      </div>

      <div class="form-group">
        <input v-model="form.email" type="email" :placeholder="$t('apply.email')" required />
        <input v-model="form.phone" type="tel" :placeholder="$t('apply.phone')" />
      </div>

      <div class="form-group">
        <input v-model="form.password" type="password" :placeholder="$t('apply.password')" required />
        <select v-model="form.education_level" required>
          <option disabled value="">{{ $t('apply.education') }}</option>
          <option value="lise">{{ $t('apply.highschool') }}</option>
          <option value="lisans">{{ $t('apply.bachelor') }}</option>
        </select>
      </div>

      <div v-if="form.education_level === 'lisans'" class="form-group">
        <input v-model="form.university_name" type="text" :placeholder="$t('apply.university')" />
        <input v-model="form.university_department" type="text" :placeholder="$t('apply.department')" />
      </div>

      <div class="form-group">
        <textarea v-model="form.experience" :placeholder="$t('apply.experience')"></textarea>
      </div>

      <div class="form-group">
        <input v-model="form.languages" type="text" :placeholder="$t('apply.languages')" />
      </div>

      <div class="form-group">
        <label>{{ $t('apply.cv') }}</label>
        <input type="file" @change="handleCVUpload" accept=".pdf,.doc,.docx" />
      </div>

      <div class="form-group">
        <label>{{ $t('apply.certificate') }}</label>
       <input type="file" multiple @change="handleCertificateUpload" accept=".pdf,.png,.jpg,.jpeg" />
      </div>

      <button type="submit" class="btn">{{ $t('apply.submit') }}</button>
    </form>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'

const { t } = useI18n()
const baseURL = process.env.VUE_APP_API_URL

const form = ref({
  name: '',
  surname: '',
  email: '',
  phone: '',
  password: '',
  education_level: '',
  university_name: '',
  university_department: '',
  experience: '',
  languages: '',
  cv_file: null,
  certificates: [],
})

function handleCVUpload(e) {
  form.value.cv_file = e.target.files[0] || null
}

function handleCertificateUpload(e) {
  form.value.certificates = Array.from(e.target.files || [])
}

function isValidEmail(email) {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return regex.test(email)
}

function isValidPassword(password) {
  // En az 8 karakter, bir büyük harf, bir küçük harf, bir rakam ve bir sembol
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/
  return regex.test(password)
}

async function submitApplication() {
  if (!isValidEmail(form.value.email)) {
    alert(t('apply.invalidEmail'))
    return
  }

  if (!isValidPassword(form.value.password)) {
    alert(t('apply.invalidPassword'))
    return
  }

  const formData = new FormData()

  // Metin alanlarını güvenli şekilde ekle (cv_file ve certificates hariç)
  Object.entries(form.value).forEach(([key, value]) => {
    if (key !== 'cv_file' && key !== 'certificates' && value !== null && value !== '') {
      formData.append(key, value)
    }
  })

  // CV dosyası varsa ekle
  if (form.value.cv_file instanceof File) {
    formData.append('cv_file', form.value.cv_file)
  }

  // Sertifikalar varsa ekle
  if (Array.isArray(form.value.certificates)) {
    form.value.certificates.forEach(file => {
      if (file instanceof File) {
        formData.append('certificates', file)
      }
    })
  }

  try {
    const res = await axios.post(`${baseURL}/apply`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })

    alert(t('apply.success'))
  } catch (err) {
    console.error(err)
    alert(t('apply.error'))
  }
}
</script>

<style scoped>
.apply-page {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 100px;
  background: #f8f9fa40;
}

.apply-form {
  background: #fff;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 700px;
}

.apply-form h2 {
  text-align: center;
  margin-bottom: 30px;
  font-size: 26px;
  color: #0c2d57;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.apply-form input,
.apply-form textarea,
.apply-form select {
  padding: 12px;
  font-size: 15px;
  border-radius: 8px;
  border: 1px solid #ccc;
  width: 100%;
}

.apply-form input:focus,
.apply-form textarea:focus,
.apply-form select:focus {
  outline: none;
  border-color: #0c2d57;
  box-shadow: 0 0 0 2px rgba(12, 45, 87, 0.2);
}

label {
  font-weight: bold;
  font-size: 14px;
  color: #333;
}

.btn {
  width: 100%;
  padding: 14px;
  background-color: #0c2d57;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: bold;
  font-size: 16px;
  cursor: pointer;
}

.btn:hover {
  background-color: #09203f;
}
@media (max-width: 768px) {
  .apply-page {
    padding-top: 60px;
    padding-left: 10px;
    padding-right: 10px;
  }

  .apply-form {
    padding: 20px;
    border-radius: 10px;
  }

  .apply-form h2 {
    font-size: 20px;
    margin-bottom: 20px;
  }

  .form-group {
    flex-direction: column;
    gap: 10px;
  }

  .apply-form input,
  .apply-form textarea,
  .apply-form select {
    font-size: 14px;
    padding: 10px;
  }

  .btn {
    padding: 12px;
    font-size: 15px;
  }
}

</style>
